<template>
  <div
    class="login-content"
    :class="{
      'login-modal': modal,
    }"
  >
    <ContentLeft v-if="!$isPure" class="login-content__left" />
    <!-- 国内登录交互 -->
    <div class="login-content__right">
      <div class="login-content__operation" :class="{ 'is-global-version': isGlobalVersion() }">
        <WechatLogin
          v-if="loginMode === 'wechat'"
          @success="onLoginSuccess"
          @accountSelect="handleAccountSelect"
          @switch="onChangeLoginModeClick"
        />
        <template v-else>
          <AccountLogin
            v-if="status === 'login'"
            @back="status = 'resetPwd'"
            @success="onLoginSuccess"
            @accountSelect="handleAccountSelect"
            @switch="onChangeLoginModeClick"
          />
          <AccountResetPwd v-else-if="status === 'resetPwd'" @back="status = 'login'" />
        </template>
      </div>
      <LicenseInfo :license="displayLicense" />
    </div>
  </div>
</template>

<script lang="ts" setup>
// 组件导入
import { usePayment } from '@rendora/common/features/payment/store';
import { showSwitchAccountModal } from '@rendora/common/features/switch-account/index';
import { getPermissionData, getUserAward, getUserInfo } from '~/api';
import { useLicense } from '~/composables/license';
import { redirectTo } from '~/utils';
import { isChinese } from '~/utils/area';
import { getEnv } from '~/utils/env';
import { emitter, EVENT } from '~/utils/eventbus';
import { IAfterLoginActionType, loginTask } from '~/utils/loginTask.ts';
import { TEMPLATE_SOURCE, TRACK_EVENT, trackEvent } from '~/utils/track.ts';
import AccountLogin from './features/login-form/ui/Base.vue';
import WechatLogin from './features/login-form/ui/LoginWechat.vue';
import AccountResetPwd from './features/reset-password/index.vue';
import ContentLeft from './shared/ui/ContentLeft.vue';
import LicenseInfo from './shared/ui/LicenseInfo.vue';

// 状态定义
type Status = 'login' | 'resetPwd';
type LoginMode = 'password' | 'wechat';

const { $isPure } = useNuxtApp();
const { isMobile } = useDevice();

const status = ref<Status>('login');
const route = useRoute();
const license = useLicense();
const req = useRequestURL();
const permissionData = usePermissionData();
const userInfo = useUserInfo();
const paymentStore = usePayment();

const props = defineProps<{
  modal: boolean;
}>();

trackEvent(TRACK_EVENT.PAGE_LOGIN, {
  isInvited: !!route.query.code,
});

// 计算属性
const displayLicense = computed(() => {
  const hostList = Object.keys(license.value || {});
  const host = hostList.find(host => req.host.endsWith(host));
  return host ? license.value[host] : null;
});

const getDefaultLoginMode = () => {
  if ($isPure || isMobile) {
    return 'password';
  }
  if (isChinese() && !getEnv('privatization')) {
    return 'wechat';
  }
  return 'password';
};

const loginMode = ref<LoginMode>(getDefaultLoginMode());

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// 处理双重编码的 redirect 参数
const getRedirectParam = () => {
  const rawRedirect = route.query?.redirect;
  if (!rawRedirect || typeof rawRedirect !== 'string') return rawRedirect;

  // 双重编码（包含 %25 表示已经编码过），需要解码两次
  if (rawRedirect.includes('%25')) {
    return safeDecode(safeDecode(rawRedirect));
  }

  // 单次编码，需要解码一次
  if (rawRedirect.includes('%')) {
    return safeDecode(rawRedirect);
  }

  // 未编码，直接返回
  return rawRedirect;
};

const redirect: any = getRedirectParam();
const isPpt = computed(() => redirect?.includes('/ppt-world'));
const isShanDong = computed(() => redirect?.includes('/shandong-innovation'));
const isHelpCenter = computed(() => redirect?.includes('/help-center'));
const isPullNew = computed(() => redirect?.includes('/pull-new'));
const isNewYear = computed(() => redirect?.includes('/new-year'));
// const isEditorPath = computed(
//   () => redirect?.includes('/editor/page/ppt') || redirect?.includes('/editor/page/chat'),
// );

const isEditorPath = computed(() => {
  if (!redirect) return false;
  const normalizedRedirect = redirect.toLowerCase();
  return (
    normalizedRedirect.includes('/editor/page/ppt') ||
    normalizedRedirect.includes('/editor/page/chat') ||
    normalizedRedirect.includes('/editor/page/ppt-script-gen') ||
    normalizedRedirect.includes('/editor/page/ppt-prompt') ||
    normalizedRedirect.includes('/editor/page/ppt-outline')
  );
});

// 方法定义
function onChangeLoginModeClick() {
  const oldMode = loginMode.value;
  loginMode.value = loginMode.value === 'password' ? 'wechat' : 'password';
  // 埋点：切换登录方式
  if (loginMode.value === 'wechat') {
    trackEvent(TRACK_EVENT.LOGIN_SWITCH_TO_WECHAT);
  } else {
    trackEvent(TRACK_EVENT.LOGIN_SWITCH_TO_PASSWORD);
  }
  trackEvent(TRACK_EVENT.LOGIN_SWITCH_MODE, {
    from: oldMode,
    to: loginMode.value,
  });
}

const handleAccountSelect = async () => {
  showSwitchAccountModal({ onLoginSuccess });
};

//  3.31日前，个人版用户登录后，补偿言币
// const getNoticeModalRightsType = () => {
//   return [
//     USER_LEVEL_KEY.PERSONAL,
//     USER_LEVEL_KEY.PERSONAL_LITE,
//   ].includes(userInfo.value?.rights_type) ? userInfo.value?.rights_type : null;
// }
const onLoginSuccess = async () => {
  // SSR 环境下不执行
  if (import.meta.server) return;

  trackEvent(TRACK_EVENT.LOGIN_SUCCESS, {
    login_way: loginMode.value,
  });

  const res = await getPermissionData();
  permissionData.value = res.data;

  const info = await getUserInfo();
  userInfo.value = info.data;

  await paymentStore.fetchDiscount();

  const { data } = await getUserAward();

  // 优先处理编辑器路径重定向
  if (redirect && isEditorPath.value) {
    if (typeof location !== 'undefined') {
      location.href = redirect;
    }
    return;
  }

  // 处理特殊登录场景
  if (
    !isPpt.value &&
    !isShanDong.value &&
    !isHelpCenter.value &&
    !isPullNew.value &&
    !isNewYear.value &&
    (data?.attraction_register_reward || data?.personal_experience_register_info)
  ) {
    if (isMobile) {
      navigateTo('/welcome?type=register');
      return;
    }
    if (typeof location !== 'undefined') {
      location.href = '/workspace';
    }
    return;
  }

  // 处理登录前任务
  if (loginTask.tasks.length > 0) {
    await loginTask.flush();
    return;
  }

  // 移动端登录
  if (isMobile) {
    if (isPullNew.value || isNewYear.value) {
      try {
        const url = new URL(redirect, location.origin);
        navigateTo(url.pathname + url.search);
      } catch {
        navigateTo('/welcome?type=login');
      }
      return;
    }
    navigateTo('/welcome?type=login');
    return;
  }

  // 处理重定向
  if (redirect) {
    if (isPullNew.value || isNewYear.value || isEditorPath.value) {
      if (typeof location !== 'undefined') {
        location.href = redirect;
      }
      return;
    }
    redirectTo(redirect);
    return;
  }

  if (typeof location !== 'undefined') {
    location.href = `/workspace`;
  }
};

// 生命周期钩子
onBeforeMount(() => {
  if (import.meta.client) {
    const urlObj = new URL(window.location.href);
    const createTempWorkId = urlObj.searchParams.get('create_temp_work');
    if (createTempWorkId) {
      loginTask.add({
        type: IAfterLoginActionType.USE_TEMPLATE,
        payload: [{ id: createTempWorkId }, TEMPLATE_SOURCE.EDITOR_CREATE],
      });
    }
  }
});

// 保存事件处理器引用，用于清理
let switchWechatHandler: (() => void) | null = null;
let findPwdHandler: (() => void) | null = null;

onMounted(() => {
  // SSR 环境下不执行
  if (import.meta.server) return;

  switchWechatHandler = () => (loginMode.value = 'wechat');
  findPwdHandler = () => (status.value = 'resetPwd');

  emitter.on(EVENT.SWITCH_WECHAT_LOGIN, switchWechatHandler);
  emitter.on(EVENT.FIND_PWD, findPwdHandler);
});

onBeforeUnmount(() => {
  // SSR 环境下不执行
  if (import.meta.server) return;

  // 清理事件监听器，避免内存泄露
  if (switchWechatHandler) {
    emitter.off(EVENT.SWITCH_WECHAT_LOGIN, switchWechatHandler);
    switchWechatHandler = null;
  }
  if (findPwdHandler) {
    emitter.off(EVENT.FIND_PWD, findPwdHandler);
    findPwdHandler = null;
  }
  loginTask.clear();
});
</script>

<style lang="scss" scoped>
@use '@rendora/design/styles/mixins.scss' as *;

.login-content__left {
  @include mobile {
    display: none;
  }
}

.login-content {
  display: flex;
  width: 100%;
  height: 100vh;
  flex-grow: 1;
  overflow: hidden;
}

/* Safari 浏览器修复：防止地址栏导致滚动条 */
@supports (-webkit-touch-callout: none) {
  .login-content {
    height: -webkit-fill-available;
  }
}

.login-content__right {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: auto;

  @include mobile {
    justify-content: flex-start;
    padding-top: responsive-size(88px);
  }
}

.login-content__operation {
  display: flex;
  flex-direction: column;

  width: 328px;
  height: 516px;

  @include mobile {
    width: fit-content;
    height: responsive-size(516px);
  }

  &.is-global-version {
    height: fit-content;
  }
}
</style>
